import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  CheckCircle2,
  AlertCircle,
  Loader2,
  Users,
  ScrollText,
  BookOpen,
  Camera,
  MessageSquare,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AdminHealthTile — renders the at-a-glance system health card used by
 * `/admin` (landing) and `/admin/analytics`.
 *
 * Reads `/api/admin/health` once on mount. Splits the response into:
 *   - "Live" stats (computed from MongoDB: user count, ToS roll-out, etc.)
 *   - "Build" snapshot (test count, type-coverage, lint status from CI env)
 *
 * Designed to fail-soft: a 403 (non-admin caller) just hides the tile;
 * any other failure shows a small "couldn't load" inline state without
 * breaking the rest of the page.
 */
const StatPill = ({ icon: Icon, label, value, sub, accent = false, "data-testid": testId }) => (
  <div
    className={`rounded-2xl border p-4 ${
      accent
        ? "bg-gradient-to-br from-[#D4B483]/15 via-[#D4B483]/5 to-transparent border-[#D4B483]/30"
        : "bg-white/[0.04] border-white/10"
    }`}
    data-testid={testId}
  >
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#D4B483] mb-2">
      <Icon className="h-3.5 w-3.5" />
      {label}
    </div>
    <div className="text-2xl font-light text-white tabular-nums">{value}</div>
    {sub && (
      <div className="text-xs text-white/55 mt-1 leading-snug">{sub}</div>
    )}
  </div>
);

const AdminHealthTile = ({ className = "" }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: payload } = await axios.get(`${API}/admin/health`, {
          withCredentials: true,
        });
        if (!cancelled) setData(payload);
      } catch (e) {
        if (cancelled) return;
        // 403 on non-admins is expected when the tile is mounted from
        // /admin/analytics by mistake — silent hide. Other errors show
        // the inline error state.
        if (e?.response?.status === 403) {
          setError("forbidden");
        } else {
          setError("network");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error === "forbidden") return null;

  if (!data && !error) {
    return (
      <div
        className={`rounded-2xl bg-white/[0.04] border border-white/10 p-6 flex items-center gap-3 text-sm text-white/60 ${className}`}
        data-testid="admin-health-loading"
      >
        <Loader2 className="h-4 w-4 animate-spin text-[#D4B483]" />
        Reading system health…
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`rounded-2xl bg-[#7F2A2A]/20 border border-[#B85F5F]/40 p-6 flex items-center gap-3 text-sm text-[#F4B8C0] ${className}`}
        data-testid="admin-health-error"
      >
        <AlertCircle className="h-4 w-4 shrink-0" />
        Couldn't load health stats — try again in a moment.
      </div>
    );
  }

  const { live, build, warnings } = data;
  const buildOk =
    build?.tests_passing === build?.tests_total &&
    build?.eslint === "clean" &&
    build?.type_coverage_pct >= 95;

  return (
    <div
      className={`bg-white/[0.03] border border-white/10 rounded-2xl p-6 sm:p-7 ${className}`}
      data-testid="admin-health-tile"
    >
      {warnings?.stripe_live_key_in_non_prod && (
        <div
          className="mb-5 rounded-xl bg-[#7F2A2A]/25 border border-[#B85F5F]/50 px-4 py-3 flex items-start gap-3 text-sm text-[#F4B8C0]"
          data-testid="admin-health-stripe-live-warning"
          role="alert"
        >
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <div className="leading-snug">
            <strong className="text-white">Stripe LIVE key in {warnings.app_env}.</strong>{" "}
            Real cards will be charged if anyone completes checkout. Swap{" "}
            <code className="bg-black/30 px-1.5 py-0.5 rounded text-[11px]">
              STRIPE_API_KEY
            </code>{" "}
            to your{" "}
            <code className="bg-black/30 px-1.5 py-0.5 rounded text-[11px]">
              sk_test_…
            </code>{" "}
            key for QA — or set{" "}
            <code className="bg-black/30 px-1.5 py-0.5 rounded text-[11px]">
              APP_ENV=production
            </code>{" "}
            to silence this.
          </div>
        </div>
      )}
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <span className="inline-block text-[10px] uppercase tracking-[0.24em] text-[#D4B483] mb-1">
            System health
          </span>
          <h2 className="text-xl font-medium text-white">
            How TruBeauté is doing
          </h2>
        </div>
        <div
          className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs ${
            buildOk
              ? "bg-[#3A7D4A]/25 text-[#A8E8B6] border border-[#3A7D4A]/40"
              : "bg-[#7D5A3A]/25 text-[#F4C988] border border-[#7D5A3A]/40"
          }`}
          data-testid="admin-health-build-status"
        >
          {buildOk ? (
            <CheckCircle2 className="h-3.5 w-3.5" />
          ) : (
            <AlertCircle className="h-3.5 w-3.5" />
          )}
          Build: {build?.tests_passing}/{build?.tests_total} tests · lint{" "}
          {build?.eslint} · {build?.type_coverage_pct}% typed
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <StatPill
          icon={Users}
          label="Total users"
          value={live.total_users}
          sub={
            live.tos_acceptance_pct === null
              ? "ToS: no users yet"
              : `ToS ${live.current_tos_version}: ${live.tos_acceptance_pct}%`
          }
          data-testid="admin-health-users"
        />
        <StatPill
          icon={ScrollText}
          label="On current ToS"
          value={live.users_on_current_tos}
          sub={`of ${live.total_users} signed-in`}
          accent
          data-testid="admin-health-tos"
        />
        <StatPill
          icon={BookOpen}
          label="Routines"
          value={live.total_routines}
          sub="all time"
          data-testid="admin-health-routines"
        />
        <StatPill
          icon={Camera}
          label="Snapshots"
          value={live.total_snapshots}
          sub="saved by users"
          data-testid="admin-health-snapshots"
        />
        <StatPill
          icon={MessageSquare}
          label="Feedback (7d)"
          value={live.feedback_last_7d}
          sub={
            live.feedback_last_7d > 0
              ? "review the inbox →"
              : "quiet week"
          }
          data-testid="admin-health-feedback"
        />
      </div>

      <p className="text-xs text-white/45 mt-5 leading-relaxed">
        Build snapshot from {build?.last_check}. Live numbers refresh on every page load.
      </p>
    </div>
  );
};

export default AdminHealthTile;
